import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { AppliedOptimization } from '@monitoring-panel/types';
import { ReportSection } from '@/components/report/ReportSection';
import { Skeleton } from '@/components/Skeleton';
import { useAppliedOptimizations } from '@/hooks/useOptimizationReports';
import { TableFrame, Th, Td } from './TableFrame';
import { dateTime, formatSuggestionValue, fullDate } from './format';
import { LEVEL_LABELS, LEVER_LABELS, SOURCE_LABELS } from './labels';
import { adviaOsRecordUrl, dv360Url, type Dv360Context } from './links';
import type { ReportLine } from './types';

interface AppliedHistoryTabProps {
  executionId: string;
  /** Lines of the report on screen, offered as the filter. */
  lines: ReportLine[];
  /** Campaign of each IO, from the report on screen, for the DV360 links. */
  links: Dv360Context;
}

const ALL_LINES = '';

export function AppliedHistoryTab({ executionId, lines, links }: AppliedHistoryTabProps) {
  const [lineItemId, setLineItemId] = useState<string>(ALL_LINES);
  const { data, isLoading, error } = useAppliedOptimizations(executionId, lineItemId || null);
  const optimizations = data?.optimizations ?? [];

  return (
    <ReportSection title="Historial aplicado" subtitle="cambios escritos en DV360 y registrados en Advia OS">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label htmlFor="history-line-filter" className="text-adv-body-s text-adv-secondary">
          Line item
        </label>
        <select
          id="history-line-filter"
          value={lineItemId}
          onChange={(event) => setLineItemId(event.target.value)}
          className="adv-input adv-input--sm min-w-[220px]"
        >
          <option value={ALL_LINES}>Todos</option>
          {lines.map((line) => (
            <option key={line.id} value={line.id}>
              {line.name}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-2/3" />
        </div>
      ) : error ? (
        <div className="adv-alert text-adv-body-s">No se pudo cargar el historial: {error.message}</div>
      ) : optimizations.length === 0 ? (
        <p className="text-adv-body-s text-adv-tertiary py-2">
          {lineItemId ? 'Ningún cambio aplicado a este line item.' : 'Aún no se ha aplicado ningún cambio.'}
        </p>
      ) : (
        <TableFrame
          head={
            <>
              <Th>Fecha</Th>
              <Th>Entidad</Th>
              <Th>Palanca</Th>
              <Th>Antes → después</Th>
              <Th>Quién</Th>
              <Th>Motivo</Th>
              <Th>Origen</Th>
            </>
          }
        >
          {optimizations.map((optimization) => (
            <AppliedRow key={optimization.recordId} optimization={optimization} links={links} />
          ))}
        </TableFrame>
      )}
    </ReportSection>
  );
}

function AppliedRow({ optimization, links }: { optimization: AppliedOptimization; links: Dv360Context }) {
  const entityId = optimization.lineItemId ?? optimization.insertionOrderId;
  const entityName = optimization.entityName ?? 'Campaña';
  const entityUrl =
    optimization.level === 'execution'
      ? null
      : dv360Url(
          {
            advertiserId: optimization.advertiserId,
            insertionOrderId: optimization.insertionOrderId,
            lineItemId: optimization.lineItemId,
          },
          links
        );

  return (
    <tr className="hover:bg-adv-sunken">
      <Td className="whitespace-nowrap">
        <span className="adv-num">{dateTime(optimization.appliedAt)}</span>
        <a
          href={adviaOsRecordUrl(optimization.recordId)}
          target="_blank"
          rel="noreferrer"
          className="adv-num text-adv-body-s text-adv-accent hover:underline inline-flex items-center gap-1"
          title="Abrir en Advia OS"
        >
          {optimization.internalId ?? 'Advia OS'}
          <ExternalLink size={12} />
        </a>
      </Td>
      <Td className="min-w-[160px]">
        {entityUrl ? (
          <a href={entityUrl} target="_blank" rel="noreferrer" className="font-bold hover:underline" title="Abrir en DV360">
            {entityName}
            <ExternalLink size={12} className="inline ml-1 align-baseline text-adv-tertiary" />
          </a>
        ) : (
          <span className="font-bold">{entityName}</span>
        )}
        <p className="text-adv-body-s text-adv-tertiary whitespace-nowrap">
          {LEVEL_LABELS[optimization.level]}
          {entityId && <span className="adv-num"> · {entityId}</span>}
        </p>
      </Td>
      <Td className="whitespace-nowrap">{LEVER_LABELS[optimization.lever]}</Td>
      <Td className="adv-num whitespace-nowrap">
        {optimization.valueBefore === null && optimization.valueAfter === null ? (
          '–'
        ) : (
          <>
            {formatSuggestionValue(optimization.unit, optimization.valueBefore, optimization.lever)}
            <span className="text-adv-tertiary"> → </span>
            <span className="font-bold">
              {formatSuggestionValue(optimization.unit, optimization.valueAfter, optimization.lever)}
            </span>
          </>
        )}
      </Td>
      <Td className="whitespace-nowrap">{optimization.appliedBy ?? '–'}</Td>
      <Td className="min-w-[220px] text-adv-secondary">
        {optimization.reason ?? '–'}
        {optimization.reportDate && (
          <p className="text-adv-body-s text-adv-tertiary">
            informe del <span className="adv-num">{fullDate(optimization.reportDate)}</span>
          </p>
        )}
      </Td>
      <Td>
        <span className={`adv-tag ${optimization.source === 'panel' ? 'adv-tag--info' : 'adv-tag--outline'}`}>
          {SOURCE_LABELS[optimization.source]}
        </span>
      </Td>
    </tr>
  );
}
